import PropTypes from 'prop-types';
import { Icon, ButtonTooltip, Popover2 } from '@evertrue/et-components';
import ConfidenceBadge from './shared/confidence-badge';
import { buildUpsellMessage } from './shared/upsell-message';
import { formatCurrency } from './utils/index';

const DRILL_DOWN_TOOLTIPS = {
  realEstate: 'View Real Estate matches',
  sec: 'View SEC Holdings matches',
};

/**
 * Displays financial signals including real estate, SEC holdings, and estimated net worth.
 */
const FinancialSignalsCard = ({
  realEstate,
  secHoldings,
  estNetWorth,
  loading = false,
  hasEnrichmentAccess = false,
  onDrillDown,
  onNetWorthClick,
  showUpsellTooltip = false,
}) => {
  const handleClick = (category, section = 'highQuality') => {
    if (hasEnrichmentAccess && onDrillDown) {
      onDrillDown(category, section);
    }
  };

  const handleNetWorthClick = () => {
    if (onNetWorthClick) {
      onNetWorthClick();
    }
  };

  const isClickable = hasEnrichmentAccess && onDrillDown;
  const isUpsell = showUpsellTooltip && !isClickable;
  const isNetWorthClickable = !!onNetWorthClick;

  return (
    <div className="financial-signals-card">
      <div className="financial-signals-card--header">
        <div className="financial-signals-card--header-left">
          <Icon icon="career" />
          <span className="financial-signals-card--title">Financial Signals</span>
        </div>
      </div>

      <div className="financial-signals-card--content">
        <div className="financial-signals-card--row">
          <div className="financial-signals-card--row-label-wrapper">
            <span className="financial-signals-card--row-label">Real Estate</span>
            <ButtonTooltip
              trigger={<Icon icon="info-circle" iconKit="gt2" className="financial-signals-card--info-icon" />}
              contentWidth={280}
              disableArrow
            >
              Estimated value and count of properties owned by a constituent. Reflects high-confidence matches only.
              Real estate holdings can be a strong indicator of philanthropic capacity.
            </ButtonTooltip>
          </div>
          {loading ? (
            <span className="financial-signals-card--row-value">
              <Icon icon="spinner" spin />
            </span>
          ) : isUpsell ? (
            <Popover2
              trigger={
                <span className="financial-signals-card--row-value">
                  <span className="giving-card--amount">{formatCurrency(realEstate?.amount) || '—'}</span>
                  <ConfidenceBadge highCount={realEstate?.highCount || 0} lowCount={realEstate?.lowCount || 0} />
                </span>
              }
              contentWidth={280}
              contentClassName="enrichment-upsell-popover"
            >
              {buildUpsellMessage('realEstate', realEstate?.highCount, realEstate?.amount)}
            </Popover2>
          ) : (
            (() => {
              const hasRealEstateData =
                realEstate?.amount || (realEstate?.highCount || 0) > 0 || (realEstate?.lowCount || 0) > 0;
              const realEstateValue = (
                <span
                  className="financial-signals-card--row-value"
                  onClick={isClickable && hasRealEstateData ? () => handleClick('realEstate') : undefined}
                  role={isClickable && hasRealEstateData ? 'button' : undefined}
                  tabIndex={isClickable && hasRealEstateData ? 0 : undefined}
                  onKeyDown={
                    isClickable && hasRealEstateData
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleClick('realEstate');
                          }
                        }
                      : undefined
                  }
                >
                  <span className="giving-card--amount">{formatCurrency(realEstate?.amount) || '—'}</span>
                  <ConfidenceBadge
                    highCount={realEstate?.highCount || 0}
                    lowCount={realEstate?.lowCount || 0}
                    onHighClick={
                      isClickable && hasRealEstateData ? () => handleClick('realEstate', 'highQuality') : undefined
                    }
                    onLowClick={
                      isClickable && hasRealEstateData ? () => handleClick('realEstate', 'lowConfidence') : undefined
                    }
                  />
                </span>
              );
              return isClickable && hasRealEstateData ? (
                <ButtonTooltip trigger={realEstateValue} contentWidth={280} disableArrow>
                  {DRILL_DOWN_TOOLTIPS.realEstate}
                </ButtonTooltip>
              ) : (
                realEstateValue
              );
            })()
          )}
        </div>

        <div className="financial-signals-card--row">
          <div className="financial-signals-card--row-label-wrapper">
            <span className="financial-signals-card--row-label">SEC Holdings</span>
            <ButtonTooltip
              trigger={<Icon icon="info-circle" iconKit="gt2" className="financial-signals-card--info-icon" />}
              contentWidth={280}
              disableArrow
            >
              Estimated value of stock ownership for a constituent who is an officer, director, or major shareholder of
              publicly traded companies. Reflects high-confidence matches only. These holdings can indicate significant
              equity ownership and philanthropic capacity.
            </ButtonTooltip>
          </div>
          {loading ? (
            <span className="financial-signals-card--row-value">
              <Icon icon="spinner" spin />
            </span>
          ) : isUpsell ? (
            <Popover2
              trigger={
                <span className="financial-signals-card--row-value">
                  <span className="giving-card--amount">{formatCurrency(secHoldings?.amount) || '—'}</span>
                  <ConfidenceBadge highCount={secHoldings?.highCount || 0} lowCount={secHoldings?.lowCount || 0} />
                </span>
              }
              contentWidth={280}
              contentClassName="enrichment-upsell-popover"
            >
              {buildUpsellMessage('sec', secHoldings?.highCount, secHoldings?.amount)}
            </Popover2>
          ) : (
            (() => {
              const hasSecData =
                secHoldings?.amount || (secHoldings?.highCount || 0) > 0 || (secHoldings?.lowCount || 0) > 0;
              const secValue = (
                <span
                  className="financial-signals-card--row-value"
                  onClick={isClickable && hasSecData ? () => handleClick('sec') : undefined}
                  role={isClickable && hasSecData ? 'button' : undefined}
                  tabIndex={isClickable && hasSecData ? 0 : undefined}
                  onKeyDown={
                    isClickable && hasSecData
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleClick('sec');
                          }
                        }
                      : undefined
                  }
                >
                  <span className="giving-card--amount">{formatCurrency(secHoldings?.amount) || '—'}</span>
                  <ConfidenceBadge
                    highCount={secHoldings?.highCount || 0}
                    lowCount={secHoldings?.lowCount || 0}
                    onHighClick={isClickable && hasSecData ? () => handleClick('sec', 'highQuality') : undefined}
                    onLowClick={isClickable && hasSecData ? () => handleClick('sec', 'lowConfidence') : undefined}
                  />
                </span>
              );
              return isClickable && hasSecData ? (
                <ButtonTooltip trigger={secValue} contentWidth={280} disableArrow>
                  {DRILL_DOWN_TOOLTIPS.sec}
                </ButtonTooltip>
              ) : (
                secValue
              );
            })()
          )}
        </div>

        <div className="financial-signals-card--row">
          <div className="financial-signals-card--row-label-wrapper">
            <span className="financial-signals-card--row-label">Est. Net Worth</span>
            <ButtonTooltip
              trigger={<Icon icon="info-circle" iconKit="gt2" className="financial-signals-card--info-icon" />}
              disableArrow
            >
              Estimated value of a constituent's total assets.
            </ButtonTooltip>
          </div>
          <span
            className={`financial-signals-card--row-value ${isNetWorthClickable ? 'financial-signals-card--row-value-clickable' : ''}`}
            onClick={isNetWorthClickable ? handleNetWorthClick : undefined}
            role={isNetWorthClickable ? 'button' : undefined}
            tabIndex={isNetWorthClickable ? 0 : undefined}
            onKeyDown={(e) => {
              if (isNetWorthClickable && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                handleNetWorthClick();
              }
            }}
          >
            {estNetWorth || '—'}
          </span>
        </div>
      </div>
    </div>
  );
};

FinancialSignalsCard.propTypes = {
  realEstate: PropTypes.shape({
    amount: PropTypes.number,
    highCount: PropTypes.number,
    lowCount: PropTypes.number,
  }),
  secHoldings: PropTypes.shape({
    amount: PropTypes.number,
    highCount: PropTypes.number,
    lowCount: PropTypes.number,
  }),
  /** Pre-formatted net worth value string (e.g., "$2.40m") from wealth_attributes */
  estNetWorth: PropTypes.string,
  loading: PropTypes.bool,
  hasEnrichmentAccess: PropTypes.bool,
  onDrillDown: PropTypes.func,
  onNetWorthClick: PropTypes.func,
  showUpsellTooltip: PropTypes.bool,
};

export default FinancialSignalsCard;
